import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Box, Loader2, Sparkles, FileArchive, Images, FlipVertical2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { DuckScene } from "../components/DuckScene";
import { FullscreenViewer } from "../components/FullscreenViewer";
import { PrintSizePicker } from "../components/PrintSizePicker";
import { OrderPrintButton } from "../components/OrderPrintButton";
import { OrderStatusCard } from "../components/OrderStatusCard";
import { ColorCountPicker } from "../components/ColorCountPicker";
import { TurntableStrip } from "../components/TurntableStrip";
import { DuckStoryCard } from "../components/DuckStoryCard";
import { DuckActionsRow } from "../components/DuckActionsRow";
import { PrintReadyBadge } from "../components/PrintReadyBadge";
import { Brand } from "../components/Brand";
import { BasketButton } from "../components/BasketDrawer";
import { QrBottomChoice, useQrDecision } from "../components/QrBottomChoice";
import api, { API, shareExportUrl, shareModelUrl, shareZipUrl, shareBeforeAfterUrl, duckTitle, DEFAULT_HEIGHT , errorText, frontImage } from "../lib/api";
import { FrontImage } from "../components/FrontImage";

export default function SharePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [duck, setDuck] = useState(null);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [underside, setUnderside] = useState(false);
  const [qrOn, setQrOn] = useState(null);
  const [colors, setColors] = useState(8);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/share/${id}`)
      .then((r) => {
        setDuck(r.data);
        if (r.data?.paint_opts?.colors) setColors(r.data.paint_opts.colors); // default to the count the duck was painted for
      })
      .catch((e) => setError(e?.response?.data?.detail || "This duck could not be loaded."));
  }, [id]);

  const ready = duck?.build_status === "ready";
  const qrDecision = useQrDecision({ creationId: id, height, owner: false, qr: qrOn });
  const qrChecked = qrOn === null ? qrDecision?.wanted !== false : qrOn;

  // Branching needs ownership (same browser session that made the duck); the backend answers 404 otherwise.
  const branch = async () => {
    setBusy("branch");
    try {
      const { data } = await api.post(`/creations/${duck.id}/branch`);
      toast.success(`Branched — version ${data.version} rendered`);
      navigate(`/?open=${data.id}`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Only the maker of this duck can branch it (from the same browser)." : errorText(e, "Could not branch this duck."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="App min-h-screen bg-dots">
      <header className="h-20 border-b border-border/70 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-8 lg:px-12 flex items-center justify-between">
          <Link to="/" data-testid="share-logo-link" className="flex items-center gap-3 group">
            <Brand />
          </Link>
          <div className="flex items-center gap-2">
            <BasketButton />
            <Link to="/">
              <Button data-testid="share-make-own-btn" className="rounded-full gap-2 font-bold">
                <Sparkles className="h-4 w-4" /> Make your own
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14">
        <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">Shared duck</span>
        <h1 data-testid="share-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2 mb-9">
          {duckTitle(duck) || "A custom rubber-duck figurine"}
        </h1>

        {error && (
          <div data-testid="share-error" className="rounded-3xl border border-destructive/40 bg-card p-8 text-center text-destructive">
            {error}
          </div>
        )}

        {!duck && !error && (
          <div className="grid place-items-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        )}

        {duck && (
          <div className="grid lg:grid-cols-[1.25fr_1fr] gap-8 animate-pop">
            <FullscreenViewer
              testId="share-duck-canvas"
              baseClassName="duck-canvas-wrap rounded-3xl border border-border overflow-hidden h-[460px] sm:h-[560px] relative"
              showButton={ready}
            >
              {ready ? (
                <>
                  <DuckScene url={shareModelUrl(duck.id)} underside={underside} />
                  {underside && (
                    <div data-testid="share-underside-banner" className="absolute top-4 left-1/2 -translate-x-1/2 w-[min(92%,30rem)] rounded-2xl bg-background/90 backdrop-blur border border-border px-4 py-2.5 text-center">
                      <p className="text-sm font-bold">Underside preview — as it will print</p>
                      <p data-testid="share-underside-note" className="text-xs text-muted-foreground mt-0.5">
                        {duck.print_options?.qr_bottom ? "Scan the printed code to come back to this page." : "This duck prints without a bottom QR code."}
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="share-underside-btn"
                    onClick={() => setUnderside((u) => !u)}
                    className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-background/80 backdrop-blur px-4 py-2 text-sm font-medium border border-border hover:bg-background"
                  >
                    <FlipVertical2 className={`h-4 w-4 ${underside ? "text-accent" : ""}`} /> {underside ? "Back to the duck" : "Underside preview"}
                  </button>
                </>
              ) : (
                <FrontImage creation={duck} alt="duck render" className="h-full w-full object-cover" />
              )}
            </FullscreenViewer>
            <div className="space-y-5 min-w-0">
              <DuckActionsRow creation={duck} height={height} onBranch={branch} busy={busy} />
              <p className="text-muted-foreground">
                Someone turned themselves into a printable rubber duck. Download the model and print it — the
                <span className="font-mono2 text-accent"> .3MF</span> opens in Bambu Studio pre-painted for multi-color, the
                <span className="font-mono2 text-accent"> .STL</span> is a single-color version for basic printers.
              </p>
              {ready ? (
                <>
                  <PrintReadyBadge creationId={duck.id} share notice={duck.export_notice} />
                  <PrintSizePicker value={height} onChange={setHeight} />
                  <ColorCountPicker value={colors} onChange={setColors} capacity={duck.slot_capacity} />
                  <OrderStatusCard id={duck.id} />
                  <OrderPrintButton id={duck.id} height={height} colors={colors} turntable={duck.turntable} render={frontImage(duck)} />
                  <a href={shareExportUrl(duck.id, "glb", height)} download data-testid="share-download-glb-btn" className="block">
                    <Button variant="outline" className="w-full min-h-12 h-auto py-3 rounded-full font-bold gap-2 whitespace-normal">
                      <Box className="h-5 w-5 shrink-0" /> Color Mixing version · textured .GLB · {height} mm
                    </Button>
                  </a>
                  <DuckStoryCard creation={duck} />
                  <TurntableStrip views={duck.turntable} />
                  {duck.share_before_after && (
                    <div data-testid="share-before-after" className="rounded-3xl border border-border bg-card/60 p-4 space-y-3">
                      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
                        <Images className="h-4 w-4" /> Before / after
                      </p>
                      <img src={shareBeforeAfterUrl(duck.id)} alt="before and after: photo to printable duck" className="w-full rounded-2xl" data-testid="share-before-after-img" />
                      <a href={shareBeforeAfterUrl(duck.id)} download data-testid="share-before-after-download" className="block">
                        <Button variant="outline" className="w-full h-11 rounded-full font-semibold gap-2">
                          <Download className="h-4 w-4" /> Download the card
                        </Button>
                      </a>
                    </div>
                  )}
                  <QrBottomChoice
                    decision={qrDecision}
                    checked={qrChecked}
                    onChange={setQrOn}
                    onSwitchHeight={setHeight}
                    testid="qr-choice-share"
                  />
                  <a href={shareExportUrl(duck.id, "3mf", height, colors, qrChecked)} download data-testid="share-download-3mf-btn" className="block">
                    <Button className="w-full h-14 rounded-full text-base font-bold gap-2 duck-shadow">
                      <Download className="h-5 w-5" /> Bambu .3MF (multi-color) · {height} mm
                    </Button>
                  </a>
                  <a href={shareExportUrl(duck.id, "stl", height)} download data-testid="share-download-stl-btn" className="block">
                    <Button variant="secondary" className="w-full h-12 rounded-full font-bold gap-2">
                      <Box className="h-5 w-5" /> Download .STL (single color)
                    </Button>
                  </a>
                  <a href={shareZipUrl(duck.id)} download data-testid="share-download-zip-btn" className="block">
                    <Button variant="ghost" className="w-full min-h-11 h-auto py-3 rounded-full font-semibold gap-2 whitespace-normal text-muted-foreground hover:text-foreground">
                      <FileArchive className="h-5 w-5 shrink-0" /> Or grab all 3 formats in one .zip · 50 mm
                    </Button>
                  </a>
                </>
              ) : (
                <p data-testid="share-not-ready" className="rounded-2xl border border-border bg-card/60 p-4 text-sm text-muted-foreground">
                  {duck.build_status === "failed" ? "The 3D build for this duck failed." : "The 3D model is still being sculpted — check back in a minute."}
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
